import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { apiUrl } from '../../api/client';
import { v4 as uuidv4 } from 'uuid';
import {
  FormGroup,
  FormInput,
  ToggleSwitch,
  Slider,
  AdvancedColorInput,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  NavButtons,
} from '../common';

const ImagesSection = ({ settings, onUpdate, onNavigate, regmailEnabled }) => {
  const prevSectionKey = () => {
    if (regmailEnabled) return 'regmailCustomization';
    if (settings.includeWalletButtons) return 'wallet';
    if (settings.includeCalendarButton) return 'calendarButton';
    if (settings.showCongressAppLinks) return 'congressAppLinks';
    return 'content';
  };
  const mergeRecentColors = useCallback((hexColors = []) => {
    try {
      const isValid = h => /^#([0-9a-fA-F]{6})$/.test(h || '');
      const incoming = hexColors
        .filter(Boolean)
        .map(c => c.toLowerCase())
        .filter(isValid);
      if (incoming.length === 0) return;
      let current = [];
      try {
        const raw = localStorage.getItem('kit:recentColors');
        current = Array.isArray(JSON.parse(raw || '[]'))
          ? JSON.parse(raw || '[]')
          : [];
      } catch {}
      const merged = [];
      incoming.forEach(c => {
        if (!merged.includes(c)) merged.push(c);
      });
      (current || []).forEach(c => {
        const lc = String(c || '').toLowerCase();
        if (!merged.includes(lc)) merged.push(lc);
      });
      const limited = merged.slice(0, 10);
      localStorage.setItem('kit:recentColors', JSON.stringify(limited));
      window.dispatchEvent(new Event('kit:recentColors'));
    } catch {}
  }, []);

  const getAuthHeaders = useCallback(() => {
    return {};
  }, []);

  const probeAttemptsRef = useRef({});
  const paletteAttemptsRef = useRef({});
  const probeControllersRef = useRef({});
  const paletteControllersRef = useRef({});
  const probeTimersRef = useRef({});
  const paletteTimersRef = useRef({});

  const detectPalette = useCallback(
    async (url, signal) => {
      try {
        const headers = {
          Accept: 'application/json',
          ...getAuthHeaders(),
        };
        const res = await fetch(
          apiUrl(`image/colors?url=${encodeURIComponent(url)}`),
          {
            headers,
            credentials: 'include',
            signal,
          }
        );
        if (!res.ok) return;
        const data = await res.json();
        if (data && Array.isArray(data.colors)) {
          mergeRecentColors(data.colors);
        }
      } catch (error) {
        if (error?.name !== 'AbortError') {
          // Ignore transient palette fetch errors.
        }
      }
    },
    [getAuthHeaders, mergeRecentColors]
  );

  const updateBanner = useCallback(
    (index, field, value) => {
      const currentBanners = settings.banners || [];
      const prevBanner = currentBanners[index] || {};
      const nextBanner = { ...prevBanner, [field]: value };

      if (field === 'url') {
        const trimmed = typeof value === 'string' ? value.trim() : value;
        const prevUrl =
          typeof prevBanner.url === 'string' ? prevBanner.url.trim() : prevBanner.url;
        nextBanner.url = trimmed;
        if (trimmed && trimmed !== prevUrl) {
          nextBanner.aspectRatioPercent = null;
          nextBanner.sizeMode = nextBanner.sizeMode || 'auto';
          const attemptKey =
            (prevBanner && (prevBanner.id || prevBanner.url)) || String(index);
          if (attemptKey && probeAttemptsRef.current[attemptKey] !== undefined) {
            delete probeAttemptsRef.current[attemptKey];
          }
          if (attemptKey && paletteAttemptsRef.current[attemptKey] !== undefined) {
            delete paletteAttemptsRef.current[attemptKey];
          }
        }
      }

      const newBanners = [...currentBanners];
      newBanners[index] = nextBanner;
      onUpdate('banners', newBanners);
    },
    [onUpdate, settings.banners]
  );

  const updateBannerFields = useCallback(
    (index, fields) => {
      const currentBanners = settings.banners || [];
      const newBanners = [...currentBanners];
      newBanners[index] = { ...newBanners[index], ...fields };
      onUpdate('banners', newBanners);
    },
    [onUpdate, settings.banners]
  );

  const detectAspectRatio = useCallback(async (url, index, signal) => {
    if (!url) return;
    // 1) Try backend probe (avoids CORS/hotlinking issues)
    try {
      const res = await fetch(
        apiUrl(`image/probe?url=${encodeURIComponent(url)}`),
        {
          headers: {
            Accept: 'application/json',
            ...getAuthHeaders(),
          },
          credentials: 'include',
          signal,
        }
      );
      if (res.ok) {
        const data = await res.json();
        if (data && data.width > 0) {
          const percent = (data.height / data.width) * 100;
          updateBannerFields(index, {
            aspectRatioPercent: parseFloat(percent.toFixed(2)),
            sizeMode: 'auto',
          });
          // Extract a color palette in the background
          detectPalette(url, signal);
          return;
        }
      }
    } catch (error) {
      if (error?.name === 'AbortError') {
        return;
      }
    }

    // 2) Fallback to client-side detection
    try {
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = url;
      });
      if (img.naturalWidth > 0) {
        const percent = (img.naturalHeight / img.naturalWidth) * 100;
        updateBannerFields(index, {
          aspectRatioPercent: parseFloat(percent.toFixed(2)),
          sizeMode: 'auto',
        });
        // Extract a color palette in the background
        detectPalette(url, signal);
      }
    } catch (error) {
      if (error?.name === 'AbortError') {
        return;
      }
    }
  }, [detectPalette, getAuthHeaders, updateBannerFields]);

  const addBanner = () => {
    const currentBanners = settings.banners || [];
    const newBanner = {
      id: uuidv4(),
      url: '',
      altText: 'Banner Image',
      location: 'top', // 'top' or 'bottom'
      placement: 'after', // 'before' or 'after' (relative to header/footer)
      height: 200,
      sizeMode: 'auto', // 'auto' | 'fixed'
      aspectRatioPercent: 33.33, // height/width * 100, used when sizeMode==='auto'
      enabled: true,
    };
    onUpdate('banners', [...currentBanners, newBanner]);
  };

  const removeBanner = index => {
    const newBanners = (settings.banners || []).filter((_, i) => i !== index);
    onUpdate('banners', newBanners);
  };

  const reorderBanner = (index, direction) => {
    const current = [...(settings.banners || [])];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= current.length) return;
    [current[index], current[newIndex]] = [current[newIndex], current[index]];
    onUpdate('banners', current);
  };

  const banners = useMemo(() => settings.banners || [], [settings.banners]);

  // Auto-detect aspect ratio for banners missing it in auto mode
  useEffect(() => {
    banners.forEach((b, idx) => {
      const key = (b && (b.id || b.url)) || String(idx);
      const attempts = probeAttemptsRef.current[key] || 0;
      const sizeMode = (b && b.sizeMode) || 'auto';
      const hasRatio = typeof b?.aspectRatioPercent === 'number' && b.aspectRatioPercent > 0;
      if (b?.url && sizeMode === 'auto' && !hasRatio && attempts < 2) {
        if (probeTimersRef.current[key]) {
          clearTimeout(probeTimersRef.current[key]);
        }
        if (probeControllersRef.current[key]) {
          probeControllersRef.current[key].abort();
        }
        probeAttemptsRef.current[key] = attempts + 1;
        probeTimersRef.current[key] = setTimeout(() => {
          const controller = new AbortController();
          probeControllersRef.current[key] = controller;
          detectAspectRatio(b.url, idx, controller.signal).finally(() => {
            if (probeControllersRef.current[key] === controller) {
              delete probeControllersRef.current[key];
            }
            delete probeTimersRef.current[key];
          });
        }, 700);
      }
    });
  }, [banners, detectAspectRatio]);

  useEffect(() => {
    banners.forEach((b, idx) => {
      const key = (b && (b.id || b.url)) || String(idx);
      if (!b?.url) return;
      const attempts = paletteAttemptsRef.current[key] || 0;
      if (attempts >= 2) return;
      if (paletteTimersRef.current[key]) {
        clearTimeout(paletteTimersRef.current[key]);
      }
      if (paletteControllersRef.current[key]) {
        paletteControllersRef.current[key].abort();
      }
      paletteAttemptsRef.current[key] = attempts + 1;
      paletteTimersRef.current[key] = setTimeout(() => {
        const controller = new AbortController();
        paletteControllersRef.current[key] = controller;
        detectPalette(b.url, controller.signal).finally(() => {
          if (paletteControllersRef.current[key] === controller) {
            delete paletteControllersRef.current[key];
          }
          delete paletteTimersRef.current[key];
        });
      }, 900);
    });
  }, [banners, detectPalette]);

  useEffect(
    () => () => {
      Object.values(probeTimersRef.current).forEach(timer => clearTimeout(timer));
      Object.values(paletteTimersRef.current).forEach(timer => clearTimeout(timer));
      Object.values(probeControllersRef.current).forEach(controller =>
        controller?.abort?.()
      );
      Object.values(paletteControllersRef.current).forEach(controller =>
        controller?.abort?.()
      );
    },
    []
  );

  return (
    <>
      <FormGroup title="Layout Options">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ToggleSwitch
            label="Add Outer Border"
            checked={settings.includeOuterBorder || false}
            onChange={v => onUpdate('includeOuterBorder', v)}
            tooltip="Adds a border around the entire email."
          />
          <ToggleSwitch
            label="Show Header Area"
            checked={settings.includeTopPadding || false}
            onChange={v => onUpdate('includeTopPadding', v)}
            tooltip="Adds a colored area at the top of the email (like the footer)."
          />
          <ToggleSwitch
            label="Show Footer"
            checked={settings.includeFooter !== false}
            onChange={v => onUpdate('includeFooter', v)}
            tooltip="Shows the footer with copyright information."
          />
        </div>
        {settings.includeOuterBorder && (
          <div className="mt-4">
            <AdvancedColorInput
              label="Outer Border Color"
              value={
                settings.outerBorderColor || settings.footerBackgroundColor
              }
              onChange={v => onUpdate('outerBorderColor', v)}
            />
          </div>
        )}
      </FormGroup>

      {settings.includeTopPadding && (
        <FormGroup title="Header Area">
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Slider
                label="Header Area Height"
                value={settings.topPaddingHeight || 20}
                onChange={v => onUpdate('topPaddingHeight', v)}
                min={10}
                max={100}
                unit="px"
              />
              <Slider
                label="Header Font Size"
                value={settings.topPaddingFontSize || settings.footerFontSize}
                onChange={v => onUpdate('topPaddingFontSize', v)}
                min={8}
                max={24}
                unit="pt"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <AdvancedColorInput
                label="Header Area Background Color"
                value={
                  settings.topPaddingBackgroundColor ||
                  settings.footerBackgroundColor
                }
                onChange={v => onUpdate('topPaddingBackgroundColor', v)}
              />
              <AdvancedColorInput
                label="Header Area Text Color"
                value={settings.topPaddingTextColor || settings.footerTextColor}
                onChange={v => onUpdate('topPaddingTextColor', v)}
              />
            </div>
            <FormInput
              label="Header Area Text"
              value={settings.topPaddingText || ''}
              onChange={e => onUpdate('topPaddingText', e.target.value)}
              placeholder="Optional text to display in the header area"
              helpText="Leave empty for just colored area, or add text like a header."
            />
          </div>
        </FormGroup>
      )}

      <FormGroup title="Banner Images">
        <div className="mb-4">
          <p className="text-sm text-gray-600">
            <strong>Banner Placement:</strong> Choose if the banner is at the
            top or bottom, and if it appears before or after the header/footer
            area.
          </p>
        </div>
        <div className="space-y-4">
          {banners.map((banner, index) => (
            <div
              key={banner.id}
              className="p-4 border rounded-lg bg-gray-50/50 space-y-4 relative"
            >
              <div className="absolute top-3 right-3 flex gap-1">
                <button
                  onClick={() => reorderBanner(index, 'up')}
                  disabled={index === 0}
                  className="p-1 rounded-full hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronUpIcon />
                </button>
                <button
                  onClick={() => reorderBanner(index, 'down')}
                  disabled={index === banners.length - 1}
                  className="p-1 rounded-full hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronDownIcon />
                </button>
                <button
                  onClick={() => removeBanner(index)}
                  className="p-1 rounded-full hover:bg-red-100"
                >
                  <TrashIcon />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormInput
                  label="Banner Image URL"
                  value={banner.url}
                  onChange={e => {
                    const url = e.target.value;
                    updateBanner(index, 'url', url);
                  }}
                  onBlur={e => {
                    const url = e.target.value;
                    if ((settings.banners?.[index]?.sizeMode || 'auto') === 'auto' && /^https?:\/\//i.test(url)) {
                      detectAspectRatio(url, index);
                    }
                  }}
                  placeholder="https://example.com/banner.png"
                  helpText="URL of the banner image."
                />
                <FormInput
                  label="Alt Text"
                  value={banner.altText}
                  onChange={e => updateBanner(index, 'altText', e.target.value)}
                  placeholder="A description of the banner"
                  helpText="Alternative text for accessibility."
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location
                  </label>
                  <select
                    value={banner.location}
                    onChange={e =>
                      updateBanner(index, 'location', e.target.value)
                    }
                    className="form-select block w-full"
                  >
                    <option value="top">Top of Email</option>
                    <option value="bottom">Bottom of Email</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Placement
                  </label>
                  {banner.location === 'top' ? (
                    <select
                      value={banner.placement}
                      onChange={e =>
                        updateBanner(index, 'placement', e.target.value)
                      }
                      className="form-select block w-full"
                    >
                      <option value="before">Before Header Area</option>
                      <option value="after">After Header Area</option>
                    </select>
                  ) : (
                    <select
                      value={banner.placement}
                      onChange={e =>
                        updateBanner(index, 'placement', e.target.value)
                      }
                      className="form-select block w-full"
                    >
                      <option value="before">Before Footer</option>
                      <option value="after">After Footer</option>
                    </select>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Size Mode
                  </label>
                  <select
                    value={banner.sizeMode || 'auto'}
                    onChange={e => updateBanner(index, 'sizeMode', e.target.value)}
                    className="form-select block w-full"
                  >
                    <option value="auto">Auto (responsive)</option>
                    <option value="fixed">Fixed height</option>
                  </select>
                </div>
              </div>

              {banner.sizeMode !== 'fixed' ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Aspect Ratio (H/W %)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={banner.aspectRatioPercent ?? 33.33}
                      onChange={e =>
                        updateBanner(
                          index,
                          'aspectRatioPercent',
                          parseFloat(e.target.value)
                        )
                      }
                      min={5}
                      max={150}
                      className="form-input block w-full"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Example: 33.33 = 3:1, 56.25 = 16:9
                    </p>
                  </div>
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={async () => {
                        if (banner.url) {
                          await detectAspectRatio(banner.url, index);
                        }
                      }}
                      className="px-3 py-2 rounded bg-gray-100 hover:bg-gray-200 text-sm"
                      disabled={!banner.url}
                    >
                      Detect from Image
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Height (px)
                    </label>
                    <input
                      type="number"
                      value={banner.height}
                      onChange={e =>
                        updateBanner(index, 'height', parseInt(e.target.value))
                      }
                      min={50}
                      max={800}
                      className="form-input block w-full"
                    />
                  </div>
                </div>
              )}
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <ToggleSwitch
                    label="Enable this banner"
                    checked={banner.enabled}
                    onChange={v => updateBanner(index, 'enabled', v)}
                  />
                </div>
                <span className="text-sm text-gray-500 flex-shrink-0">
                  {banner.location === 'top'
                    ? `Top Banner (${banner.placement === 'before' ? 'Before Header Area' : 'After Header Area'})`
                    : `Bottom Banner (${banner.placement === 'before' ? 'Before Footer' : 'After Footer'})`}
                </span>
              </div>
            </div>
          ))}
        </div>
        <button
          onClick={addBanner}
          className="mt-4 text-sm font-semibold text-primary hover:text-blue-800"
        >
          + Add Banner Image
        </button>
      </FormGroup>
      <NavButtons
        onPrev={() => onNavigate(prevSectionKey())}
        onNext={() => onNavigate('contacts')}
      />
    </>
  );
};

export default ImagesSection;
